import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/Context';
import toast from 'react-hot-toast';
import { SocketContext } from '../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import TouchButton from '../../components/TouchButton/TouchButton';
import SearchIcon from '@mui/icons-material/Search';
import {
  headerCellSx,
  dataRowSx,
  timeCellSx,
  touchMenuProps,
} from '../../theme/tableTokens';
import TableLoader from '../../components/TableLoader/TableLoader';
import '../../assets/css/buttons.css';

// ── Shared input styles ────────────────────────────────────────────────────────

const selectSx = {
  color: 'white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#365ab8' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fcad89' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fcad89' },
  '& .MuiSvgIcon-root': { color: 'white' },
};

const textFieldSx = {
  width: '100%',
  backgroundColor: '#232a45',
  '& .MuiOutlinedInput-root': {
    height: '90px',
    '& fieldset': { borderColor: '#365ab8' },
    '&:hover fieldset': { borderColor: '#fcad89' },
    '&.Mui-focused fieldset': { borderColor: '#fcad89' },
  },
  '& input': { color: 'white', fontSize: '24px' },
  '& label': { color: 'white !important', fontSize: '20px' },
  '& label.Mui-focused': { color: 'white !important' },
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
    transform: 'translate(14px, 30px) scale(1)',
  },
};


// ── Game chip colours ──────────────────────────────────────────────────────────

const GAME_COLORS = {
  PowerBingo:  { bg: '#4a1070', color: '#ce93d8' },
  Bonuts:      { bg: '#0d3b7a', color: '#90caf9' },
  BingoTown:   { bg: '#004d40', color: '#80cbc4' },
  LuckyBubble: { bg: '#7a3800', color: '#ffcc80' },
  Kingo:       { bg: '#311b92', color: '#b39ddb' },
  Xtipp:       { bg: '#1b5e20', color: '#a5d6a7' },
  XtippPapir:  { bg: '#33691e', color: '#dcedc8' },
  Cashier:     { bg: '#263238', color: '#b0bec5' },
  Kiosk:       { bg: '#3e2723', color: '#bcaaa4' },
};

// ── Action badge colours ───────────────────────────────────────────────────────

const ACTION_STYLES = {
  11:               { bg: '#1b4020', color: '#69f0ae' },   // Win
  12:               { bg: '#0d1b4e', color: '#82b1ff' },   // Buy cards
  13:               { bg: '#004d40', color: '#64ffda' },   // Deposit
  14:               { bg: '#4a0000', color: '#ef9a9a' },   // Cancel
  15:               { bg: '#004d40', color: '#64ffda' },   // Deposit
  16:               { bg: '#004d40', color: '#64ffda' },   // Deposit prev session
  'Opened session': { bg: '#1a3a1a', color: '#a5d6a7' },
  'Closed session': { bg: '#2d2d2d', color: '#bdbdbd' },
};

// ─────────────────────────────────────────────────────────────────────────────

const GAME_LABELS = {
  powerbingo:  'PowerBingo',
  bonuts:      'Bonuts',
  bingotown:   'BingoTown',
  luckybubble: 'LuckyBubble',
  kingo:       'Kingo',
  xtipp:       'Xtipp',
  xtipppapir:  'XtippPapir',
};

function BilletHistorikk() {
  const [rows, setRows] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchType, setSearchType] = useState('userID');
  const [searchValue, setSearchValue] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [resolvedUserID, setResolvedUserID] = useState(null);

  const buttonRef = useRef(null);
  const dateInputRef = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLogin, isBlocking, BlockingScreen, operator, session } =
    useContext(AppContext);
  const { socket } = useContext(SocketContext);

  useEffect(() => {
    if (!isLogin) navigate('/login');
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') event.preventDefault();
      if (event.key === 'Enter') buttonRef.current?.click();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleTicketHistoric = (data) => {
      setIsLoading(false);
      if (data.error) {
        toast.error(data.error);
        setRows([]);
        return;
      }

      setResolvedUserID(String(data.userID));
      const movements = [...(data.pos || [])];

      for (const [key, label] of Object.entries(GAME_LABELS)) {
        const platform = data[key];
        if (!platform) continue;
        const { sessions, transactions } = platform;
        if (sessions) {
          movements.push(
            ...(sessions.sessionsListOpened || []).map((r) => ({ ...r, game: label })),
            ...(sessions.sessionsListClosed || []).map((r) => ({ ...r, game: label }))
          );
        }
        movements.push(...(transactions || []).map((r) => ({ ...r, game: label })));
      }

      movements.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRows(movements);
    };

    socket.on('ticketHistoric', handleTicketHistoric);
    return () => socket.off('ticketHistoric', handleTicketHistoric);
  }, [socket]);

  const searchUser = () => {
    if (!searchValue.trim()) {
      toast.error('Please enter a search value');
      return;
    }
    setIsLoading(true);
    setRows(false);
    socket.emit('ticketHistoric', {
      operator,
      sessionID: session,
      bingoName: 'Alle bingohaller',
      date,
      searchType,
      searchValue: searchValue.trim(),
    });
  };

  // ── Renderers ───────────────────────────────────────────────────────────────

  function convertToTime(isoString) {
    const d = new Date(isoString);
    const pad = (n) => (n < 10 ? '0' + n : n);
    return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  }

  function getActionLabel(action) {
    switch (action) {
      case 'Opened session': return t('ticketHistoric.openSession');
      case 'Closed session': return t('ticketHistoric.closeSession');
      case 11: return t('ticketHistoric.win');
      case 12: return t('ticketHistoric.payment');
      case 13: return t('ticketHistoric.deposit');
      case 14: return t('ticketHistoric.cancel');
      case 15: return t('ticketHistoric.deposit');
      case 16: return t('ticketHistoric.depositFromPreviousSession');
      default: return typeof action === 'string' ? action : '';
    }
  }

  function ActionChip({ action }) {
    const label = getActionLabel(action);
    if (!label) return null;
    const style = ACTION_STYLES[action] || { bg: '#263238', color: '#90a4ae' };
    return (
      <Chip
        label={label}
        sx={{
          backgroundColor: style.bg,
          color: style.color,
          fontWeight: 600,
          fontSize: '13px',
          border: `1px solid ${style.color}44`,
          height: '28px',
        }}
      />
    );
  }

  function GameChip({ game }) {
    if (!game) return null;
    const style = GAME_COLORS[game] || { bg: '#263238', color: '#90a4ae' };
    return (
      <Chip
        label={game}
        sx={{
          backgroundColor: style.bg,
          color: style.color,
          fontWeight: 700,
          fontSize: '13px',
          height: '28px',
        }}
      />
    );
  }

  function OwnerChip({ ownerID }) {
    const isTicket = resolvedUserID && String(ownerID) !== resolvedUserID;
    return (
      <Chip
        label={isTicket ? `#${ownerID}` : ownerID}
        sx={{
          backgroundColor: isTicket ? '#4a2c00' : '#0d1b3e',
          color: isTicket ? '#ffb74d' : '#7986cb',
          fontWeight: 600,
          fontSize: '11px',
          border: isTicket ? '1px solid #ff980044' : '1px solid #3f51b544',
          height: '22px',
        }}
      />
    );
  }

  function ValueCell({ value }) {
    if (value === null || value === undefined || value === '') return <span style={{ color: '#4a5a8a' }}>—</span>;
    const num = parseFloat(value);
    if (isNaN(num)) return <span>{value}</span>;
    const color = num > 0 ? '#69f0ae' : num < 0 ? '#ef9a9a' : '#8fa3d4';
    return (
      <Box sx={{ color, fontWeight: 700 }}>
        {num}
      </Box>
    );
  }

  const searchTypeLabel = {
    userID: t('ticketHistoric.userID'),
    phone: t('ticketHistoric.phone'),
    username: t('ticketHistoric.username'),
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {isBlocking && <BlockingScreen />}
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{ maxWidth: '100%', width: '100%', height: '100%', overflow: 'hidden' }}
      >
        <Grid item xs={12} sx={{ width: '100%', height: '100%' }}>
          <Grid container sx={{ width: '100%', height: '100%', flexDirection: 'row' }}>

            {/* SEARCH BAR */}
            <Grid
              item
              xs={12}
              sx={{
                height: '190px',
                maxHeight: '190px',
                display: 'flex',
                alignItems: 'center',
                px: 3,
                gap: 2,
                borderBottom: '1px solid #2a3a6e',
              }}
            >
              <FormControl sx={{ minWidth: 180, backgroundColor: '#232a45', borderRadius: '4px' }}>
                <InputLabel sx={{ color: 'white !important', fontSize: '20px' }}>
                  {t('ticketHistoric.searchType')}
                </InputLabel>
                <Select
                  MenuProps={touchMenuProps}
                  value={searchType}
                  label={t('ticketHistoric.searchType')}
                  onChange={(e) => setSearchType(e.target.value)}
                  sx={{ ...selectSx, height: '90px', fontSize: '24px' }}
                >
                  <MenuItem value="userID">{t('ticketHistoric.userID')}</MenuItem>
                  <MenuItem value="phone">{t('ticketHistoric.phone')}</MenuItem>
                  <MenuItem value="username">{t('ticketHistoric.username')}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                sx={{ ...textFieldSx, flex: 1 }}
                label={searchTypeLabel[searchType]}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
                autoComplete="off"
                variant="outlined"
              />

              <TextField
                type="date"
                label={t('ticketHistoric.date')}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputRef={dateInputRef}
                onClick={() => dateInputRef.current?.showPicker?.()}
                sx={{
                  ...textFieldSx,
                  width: '310px',
                  cursor: 'pointer',
                  '& .MuiOutlinedInput-root': {
                    height: '90px',
                    cursor: 'pointer',
                    '& fieldset': { borderColor: '#365ab8' },
                    '&:hover fieldset': { borderColor: '#fcad89' },
                    '&.Mui-focused fieldset': { borderColor: '#fcad89' },
                  },
                  '& input': {
                    color: 'white',
                    fontSize: '24px',
                    colorScheme: 'dark',
                    cursor: 'pointer',
                  },
                }}
              />

              <TouchButton
                tone="confirm"
                ref={buttonRef}
                onClick={searchUser}
                startIcon={<SearchIcon />}
                sx={{ height: '90px', minWidth: '160px', flexShrink: 0 }}
              >
                {t('menu.search')}
              </TouchButton>
            </Grid>

            {/* TABLE */}
            <Grid item xs={12} sx={{ width: '100%', padding: '10px', height: '100%' }}>
              <TableContainer
                component={Paper}
                className="sp-scroll"
                sx={{
                  maxHeight: 'calc(100% - 190px)',
                  marginBottom: '190px',
                  overflowY: 'auto',
                  backgroundColor: '#1a2240',
                }}
              >
                {isLoading ? (
                  <TableLoader />
                ) : rows !== false && rows.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '240px',
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ color: '#4a5a8a', fontSize: '18px', fontWeight: 500 }}>
                      {t('ticketHistoric.noResults')}
                    </Typography>
                  </Box>
                ) : (
                  <Table
                    sx={{ color: 'white', background: '#1a2240' }}
                    stickyHeader
                    aria-label="simple table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" sx={headerCellSx}>{t('sales.time')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.ownerID')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.game')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.action')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.valueChange')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.saldoInit')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.saldoEnd')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.gameID')}</TableCell>
                        <TableCell align="center" sx={headerCellSx}>{t('ticketHistoric.bingoName')}</TableCell>
                      </TableRow>
                    </TableHead>
                    {rows && (
                      <TableBody>
                        {rows.map((row, index) => {
                          const isTicketRow =
                            resolvedUserID && String(row.ownerID) !== resolvedUserID;
                          return (
                            <TableRow
                              key={index}
                              sx={dataRowSx}
                            >
                              <TableCell align="center" sx={timeCellSx}>
                                {convertToTime(row.date)}
                              </TableCell>
                              <TableCell align="center">
                                <OwnerChip ownerID={row.ownerID} />
                              </TableCell>
                              <TableCell align="center">
                                <GameChip game={row.game} />
                              </TableCell>
                              <TableCell align="center">
                                <ActionChip action={row.action} />
                              </TableCell>
                              <TableCell align="center">
                                <ValueCell value={row.valueChange} />
                              </TableCell>
                              <TableCell align="center" sx={{ color: '#8fa3d4' }}>
                                {row.saldoInit ?? '—'}
                              </TableCell>
                              <TableCell align="center" sx={{ color: '#8fa3d4' }}>
                                {row.saldoEnd ?? '—'}
                              </TableCell>
                              <TableCell align="center" sx={{ color: '#8fa3d4' }}>
                                {row.gameID ?? '—'}
                              </TableCell>
                              <TableCell align="center" sx={{ color: '#8fa3d4' }}>
                                {row.bingoName ?? '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    )}
                  </Table>
                )}
              </TableContainer>
            </Grid>

          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default BilletHistorikk;
